import {afterNextRender, Injectable} from '@angular/core';
import type * as Plyr from 'plyr';
import Hls from 'hls.js';
import {MediaObjectInterface} from '../../core/interfaces/media-object.interface';
import {KeyValue} from "@angular/common";
import {Capacitor} from '@capacitor/core';
import {MediaOfflineService} from "../../core/services/media-offline.service";
import {BehaviorSubject} from 'rxjs';
import {EmbeddedVideoPlayerService} from '../media/embedded-video-player.service';
import {VolumeButtonsService} from '../../core/services/volume-buttons.service';

export type VideoDecoderMode = 'active' | 'warm' | 'inactive';

export interface VideoPlayerOptions {
  mode?: Exclude<VideoDecoderMode, 'inactive'>;
  resourceKey?: string;
  scope?: string;
  /** Warm, hidden decoders must never turn their technical play into offline pinning. */
  cacheForOfflineOnPlay?: boolean;
  /** Modal playback must not wait for the optional Plyr UI module. */
  startPlaybackImmediately?: boolean;
}

/**
 * One-use capability for moving a bounded Media-grid warm decoder into the
 * gallery. The native element and HLS instance intentionally stay private to
 * VideoService: consumers can only claim/adopt or dispose this capability.
 */
export interface ViewportDecoderHandoff {
  readonly token: string;
  readonly mediaId: string | number;
}

interface RegisteredTimelineVideo {
  id: string;
  element: HTMLVideoElement;
  onDecoderReleased?: () => void;
  autoplayAttempt: number;
  autoplayState: 'idle' | 'attempting' | 'playing' | 'retry-pending';
  autoplayReadyCleanup?: () => void;
  autoplayRetryTimer?: ReturnType<typeof setTimeout>;
}

interface DecoderReadinessWaiter {
  owner: HTMLVideoElement;
  resolve: (ready: boolean) => void;
  cleanup: () => void;
}

/**
 * A bare HLS pipeline used by the Media grid.  It deliberately has no Plyr
 * instance and is owned by the virtual viewport, not by a tile component.
 * Keeping it separate from `players` prevents an invisible grid warm-up from
 * becoming a second interactive player or from competing with Timeline
 * autoplay registrations.
 */
interface ViewportWarmDecoder {
  key: string;
  scope: string;
  element: HTMLVideoElement;
  hls?: Hls;
  resolvedSource?: string;
  /**
   * `buffered` means that this exact MediaSource can render immediately.
   * `pending` may still move into the selected gallery item, preserving its
   * in-flight source instead of rebuilding a second HLS pipeline.
   */
  state: 'pending' | 'buffered' | 'claimed' | 'adopted' | 'invalid';
  sourceReady: Promise<boolean>;
  resolveSourceReady: (ready: boolean) => void;
  /** Resolves only after the startup range is really present in this MSE. */
  bufferedReady: Promise<boolean>;
  resolveBufferedReady: (ready: boolean) => void;
  /** Fixed target avoids an autoplaying muted element extending its warm-up indefinitely. */
  bufferTargetEnd: number;
  stopTimer?: ReturnType<typeof setTimeout>;
  nativeWarmCleanup?: () => void;
  /** Pool callbacks are explicitly owned so a role switch never removes a new owner's listeners. */
  hlsListenerCleanup?: () => void;
  /** Only a fully buffered pipeline returned from a gallery gets the fast-fail recovery path. */
  returnedFromGallery?: boolean;
}

interface HlsEventListeners {
  hls: Hls;
  manifestParsed: any;
  fragBuffered: any;
  error: any;
}

@Injectable({
  providedIn: 'root',
})
export class VideoService {
  private readonly autoplayRootMargin = '60% 0px 60% 0px';
  private readonly centerLineHysteresisPx = 24;
  private readonly timelineVideos = new Map<string, RegisteredTimelineVideo>();
  private intersectionObserver: IntersectionObserver | null = null;
  private viewportListenersBound = false;
  private animationFrameId: number | null = null;
  private autoplaySuppressed = false;
  private lastScrollDirection: 'up' | 'down' = 'down';
  private boundViewportChange = () => this.scheduleViewportEvaluation();
  private players: { [key: string]: Plyr } = {};
  private hlses: { [key: string]: Hls } = {};
  private readonly hlsSources = new Map<string, string>();
  private readonly resourceOwners = new Map<string, HTMLVideoElement>();
  private readonly resourceScopes = new Map<string, string>();
  /** Modal-only decoder mode; Timeline cards keep their source while mounted. */
  private readonly decoderModes = new Map<string, VideoDecoderMode>();
  /** One definitive autoplay retry after the first media fragment becomes playable. */
  private readonly bufferedAutoplayAttempts = new Set<string>();
  /** Warm gallery neighbours stop only after a bounded startup buffer is ready. */
  private readonly warmBufferedVideoKeys = new Set<string>();
  /** A muted warm play decodes the first frame before the slide becomes visible. */
  private readonly warmDecoderPrimeCleanup = new Map<string, () => void>();
  private readonly decoderReadinessWaiters = new Map<string, Set<DecoderReadinessWaiter>>();
  private readonly viewportWarmDecoders = new Map<string, ViewportWarmDecoder>();
  private readonly viewportWarmHosts = new Map<string, HTMLElement>();
  /** Real, non-overscanned media ids eligible to receive a decoder back from a gallery. */
  private readonly viewportWarmVisibleKeys = new Map<string, Set<string>>();
  private readonly viewportDecoderHandoffs = new Map<string, ViewportWarmDecoder>();
  /** Gallery resource key -> its adopted viewport decoder, retained only for a possible close handoff. */
  private readonly adoptedViewportWarmDecoders = new Map<string, ViewportWarmDecoder>();
  /** Gallery HLS callbacks are scoped by resource key rather than removed globally. */
  private readonly hlsEventListeners = new Map<string, HlsEventListeners>();
  /** A returned four-second pipeline should become ready immediately; otherwise retry once cold. */
  private readonly adoptedViewportFallbackKeys = new Set<string>();
  private readonly failedViewportDecoderFallbackKeys = new Set<string>();
  private viewportDecoderHandoffHost: HTMLElement | null = null;
  private viewportDecoderHandoffSequence = 0;
  private readonly warmBufferTargetSeconds = 4;
  private hlsStartLoadCalls = 0;
  private hlsStopLoadCalls = 0;
  private hlsBufferedFragments = 0;
  private hlsErrors = 0;
  public currentVideoId: string | null = null;
  private lastScroll: number | null = null;
  private readonly mutedSubject = new BehaviorSubject<boolean>(true);
  public readonly muted$ = this.mutedSubject.asObservable();
  private readonly offlineCachingBoundVideos = new WeakSet<HTMLVideoElement>();
  private plyrModule: any;
  private plyrModulePromise: Promise<any> | null = null;
  public eventListeners: KeyValue<any, KeyValue<any, EventListener>>[] = [];
  private readonly isNativeIos = Capacitor.getPlatform() === 'ios';
  private activePlayerInitializations = 0;
  private startedPlayerInitializations = 0;
  private cancelledPlayerInitializations = 0;
  private autoplayAttempts = 0;
  private autoplayStarts = 0;
  private autoplayRejections = 0;
  private autoplayRetries = 0;
  private releasedDecoders = 0;
  private readonly autoplayFailureReasons: Record<'abort' | 'notAllowed' | 'source' | 'other', number> = {
    abort: 0,
    notAllowed: 0,
    source: 0,
    other: 0,
  };

  constructor(
    private readonly mediaOffline: MediaOfflineService,
    private readonly embeddedVideoPlayer: EmbeddedVideoPlayerService,
    private readonly volumeButtons: VolumeButtonsService
  ) {
    afterNextRender(() => {
      void this.preloadPlayerRuntime().catch(() => undefined);
    });

    this.volumeButtons.volumeUpPressed$.subscribe(() => {
      this.setMuted(false);
    });

    this.updateVolumeButtonsWatch(this.mutedSubject.value);
  }

  get muted(): boolean {
    return this.mutedSubject.value;
  }

  /**
   * Starts loading the player code without allocating a player, HLS instance,
   * media element or decoded buffer. Consumers can call this before an
   * interaction whose first video would otherwise pay the dynamic-import cost.
   */
  public preloadPlayerRuntime(): Promise<void> {
    return this.loadPlyrModule().then(() => undefined);
  }

  private loadPlyrModule(): Promise<any> {
    if (this.plyrModule) {
      return Promise.resolve(this.plyrModule);
    }
    if (!this.plyrModulePromise) {
      this.plyrModulePromise = import('plyr')
        .then((module) => {
          this.plyrModule = module;
          return module;
        })
        .catch((error) => {
          this.plyrModulePromise = null;
          throw error;
        });
    }
    return this.plyrModulePromise;
  }

  /** Lightweight counters for diagnosing retained video runtime resources. */
  public getDiagnostics(): {
    registeredTimelineVideos: number;
    players: number;
    hlsInstances: number;
    resourceOwners: number;
    eventListeners: number;
    activePlayerInitializations: number;
    startedPlayerInitializations: number;
    cancelledPlayerInitializations: number;
    autoplayAttempts: number;
    autoplayStarts: number;
    autoplayRejections: number;
    autoplayRetries: number;
    autoplayFailureReasons: Readonly<Record<'abort' | 'notAllowed' | 'source' | 'other', number>>;
    releasedDecoders: number;
    activeVideoId: string | null;
    scopedResources: number;
    hlsStartLoadCalls: number;
    hlsStopLoadCalls: number;
    hlsBufferedFragments: number;
    warmBufferedDecoders: number;
    hlsErrors: number;
    viewportWarmDecoders: number;
    viewportDecoderHandoffs: number;
    pendingViewportWarmDecoders: number;
  } {
    return {
      registeredTimelineVideos: this.timelineVideos.size,
      players: Object.keys(this.players).length,
      hlsInstances: Object.keys(this.hlses).length,
      resourceOwners: this.resourceOwners.size,
      eventListeners: this.eventListeners.length,
      activePlayerInitializations: this.activePlayerInitializations,
      startedPlayerInitializations: this.startedPlayerInitializations,
      cancelledPlayerInitializations: this.cancelledPlayerInitializations,
      autoplayAttempts: this.autoplayAttempts,
      autoplayStarts: this.autoplayStarts,
      autoplayRejections: this.autoplayRejections,
      autoplayRetries: this.autoplayRetries,
      autoplayFailureReasons: {...this.autoplayFailureReasons},
      releasedDecoders: this.releasedDecoders,
      activeVideoId: this.currentVideoId,
      scopedResources: this.resourceScopes.size,
      hlsStartLoadCalls: this.hlsStartLoadCalls,
      hlsStopLoadCalls: this.hlsStopLoadCalls,
      hlsBufferedFragments: this.hlsBufferedFragments,
      warmBufferedDecoders: this.warmBufferedVideoKeys.size,
      hlsErrors: this.hlsErrors,
      viewportWarmDecoders: this.viewportWarmDecoders.size,
      viewportDecoderHandoffs: this.viewportDecoderHandoffs.size,
      pendingViewportWarmDecoders: [...this.viewportWarmDecoders.values()]
        .filter((decoder) => decoder.state === 'pending').length,
    };
  }

  /**
   * Reconciles the grid's real on-screen videos with a bounded set of bare
   * muted HLS pipelines.  This is intentionally not a cache-only prewarm:
   * the visible tiles receive a manifest plus a short decoded startup buffer,
   * while a tile that leaves the viewport is synchronously destroyed.
   *
   * The pool owns no Plyr and no Timeline registration.  It therefore cannot
   * autoplay off-screen, add controls, or retain a virtual card after the
   * current viewport changes.
   */
  public reconcileViewportWarmDecoders(
    scope: string,
    host: HTMLElement | null | undefined,
    media: MediaObjectInterface[],
  ): void {
    if (!scope || !host || !this.isBrowserEnvironment()) {
      return;
    }

    const desired = new Map<string, MediaObjectInterface>();
    media.forEach((mediaObject) => {
      const source = this.getPlaylistSource(mediaObject);
      if (!source) {
        return;
      }
      // A resource key is local to this virtual Timeline instance.  Recycled
      // cards and concurrently mounted swipe previews cannot overwrite it.
      desired.set(`${scope}:${mediaObject.id || source}`, mediaObject);
    });
    this.viewportWarmVisibleKeys.set(scope, new Set(desired.keys()));

    [...this.viewportWarmDecoders.entries()]
      .filter(([key, decoder]) => decoder.scope === scope && !desired.has(key))
      .forEach(([key]) => this.disposeViewportWarmDecoder(key));
    [...this.viewportDecoderHandoffs.entries()]
      .filter(([, decoder]) => decoder.scope === scope && !desired.has(decoder.key))
      .forEach(([token, decoder]) => {
        this.viewportDecoderHandoffs.delete(token);
        this.disposeDetachedViewportWarmDecoder(decoder);
      });
    [...this.adoptedViewportWarmDecoders.entries()]
      .filter(([, decoder]) => decoder.scope === scope && !desired.has(decoder.key))
      .forEach(([resourceKey, decoder]) => this.disposeVideo(resourceKey, decoder.element));

    if (!desired.size) {
      this.viewportWarmVisibleKeys.delete(scope);
      this.removeViewportWarmHost(scope);
      return;
    }

    const poolHost = this.getViewportWarmHost(scope, host);
    desired.forEach((mediaObject, key) => {
      const isClaimedOrAdopted = [...this.viewportDecoderHandoffs.values()]
        .concat([...this.adoptedViewportWarmDecoders.values()])
        .some((decoder) => decoder.key === key && decoder.scope === scope);
      if (!this.viewportWarmDecoders.has(key) && !isClaimedOrAdopted) {
        void this.createViewportWarmDecoder(key, scope, poolHost, mediaObject);
      }
    });
  }

  /** Releases every hidden decoder owned by a destroyed/hidden virtual grid. */
  public disposeViewportWarmScope(scope: string | null | undefined): void {
    if (!scope) {
      return;
    }
    [...this.viewportWarmDecoders.entries()]
      .filter(([, decoder]) => decoder.scope === scope)
      .forEach(([key]) => this.disposeViewportWarmDecoder(key));
    [...this.viewportDecoderHandoffs.entries()]
      .filter(([, decoder]) => decoder.scope === scope)
      .forEach(([token, decoder]) => {
        this.viewportDecoderHandoffs.delete(token);
        this.disposeDetachedViewportWarmDecoder(decoder);
      });
    [...this.adoptedViewportWarmDecoders.entries()]
      .filter(([, decoder]) => decoder.scope === scope)
      .forEach(([resourceKey, decoder]) => this.disposeVideo(resourceKey, decoder.element));
    this.viewportWarmVisibleKeys.delete(scope);
    this.removeViewportWarmHost(scope);
    this.removeViewportDecoderHandoffHostIfEmpty();
  }

  /**
   * Atomically transfers up to the active Media tile and its two immediate
   * video neighbours.  The gallery keeps the selected decoder active and the
   * others warm; every other decoder in this viewport scope is released.
   */
  public claimViewportWarmDecoders(
    scope: string | null | undefined,
    mediaIds: Array<string | number | null | undefined>,
  ): ViewportDecoderHandoff[] {
    if (!this.isBrowserEnvironment() || !scope) {
      return [];
    }
    const requestedIds = [...new Set(mediaIds.filter((mediaId): mediaId is string | number => mediaId !== null && mediaId !== undefined))]
      .slice(0, 3);
    // A handoff is valuable as soon as the exact source has been attached.
    // Waiting for four seconds here made a quick tap discard the in-flight
    // HLS/MSE work and forced the gallery to create a second pipeline. The
    // four-second range remains the "spinner may be skipped" threshold, not
    // a requirement for ownership transfer.
    const selectedDecoder = requestedIds.length
      ? this.viewportWarmDecoders.get(`${scope}:${requestedIds[0]}`)
      : undefined;
    if (!selectedDecoder || !this.isViewportWarmDecoderTransferable(selectedDecoder)) {
      return [];
    }
    const claimed = requestedIds
      .map((mediaId) => ({mediaId, key: `${scope}:${mediaId}`, decoder: this.viewportWarmDecoders.get(`${scope}:${mediaId}`)}))
      .filter((candidate): candidate is {mediaId: string | number; key: string; decoder: ViewportWarmDecoder} =>
        !!candidate.decoder && this.isViewportWarmDecoderTransferable(candidate.decoder));

    if (!claimed.length) {
      return [];
    }

    claimed.forEach(({key}) => this.viewportWarmDecoders.delete(key));
    [...this.viewportWarmDecoders.entries()]
      .filter(([, candidate]) => candidate.scope === scope)
      .forEach(([candidateKey]) => this.disposeViewportWarmDecoder(candidateKey));

    const transferHost = this.getViewportDecoderHandoffHost();
    return claimed.map(({mediaId, decoder}) => {
      decoder.state = 'claimed';
      transferHost.appendChild(decoder.element);
      const handoff: ViewportDecoderHandoff = {
        token: `viewport-decoder-${++this.viewportDecoderHandoffSequence}`,
        mediaId,
      };
      this.viewportDecoderHandoffs.set(handoff.token, decoder);
      return handoff;
    });
  }

  /** Compatibility wrapper for callers that transfer only the selected tile. */
  public claimViewportWarmDecoder(
    scope: string | null | undefined,
    mediaId: string | number | null | undefined,
  ): ViewportDecoderHandoff | null {
    return this.claimViewportWarmDecoders(scope, [mediaId])[0] || null;
  }

  /**
   * Moves the exact warm native element and HLS ownership to the gallery
   * resource key. No attachMedia/loadSource call is made after this point.
   */
  public adoptViewportWarmDecoder(
    handoff: ViewportDecoderHandoff | null | undefined,
    mediaId: string | number | null | undefined,
    resourceKey: string | null | undefined,
    scope: string | null | undefined,
    templateVideo: HTMLVideoElement | null | undefined,
  ): HTMLVideoElement | null {
    if (!this.isBrowserEnvironment() || !handoff || handoff.mediaId !== mediaId || !resourceKey || !templateVideo) {
      return null;
    }
    const decoder = this.viewportDecoderHandoffs.get(handoff.token);
    if (!decoder || !this.isViewportWarmDecoderAdoptable(decoder)) {
      if (decoder) {
        this.viewportDecoderHandoffs.delete(handoff.token);
        this.disposeDetachedViewportWarmDecoder(decoder);
      }
      return null;
    }
    this.viewportDecoderHandoffs.delete(handoff.token);
    const wasBuffered = decoder.state === 'buffered';
    decoder.state = 'adopted';
    templateVideo.replaceWith(decoder.element);
    this.restoreViewportDecoderElement(decoder.element, templateVideo);
    this.resourceOwners.set(resourceKey, decoder.element);
    if (scope) {
      this.resourceScopes.set(resourceKey, scope);
    }
    this.decoderModes.set(resourceKey, 'warm');
    this.adoptedViewportWarmDecoders.set(resourceKey, decoder);
    // The viewport pool keeps HLS attached and lets its four-second buffer
    // limit provide backpressure. That avoids a stop/start scheduler cycle
    // when this exact element moves into the gallery.
    if (decoder.hls && wasBuffered) {
      this.warmBufferedVideoKeys.add(resourceKey);
    }
    if (decoder.returnedFromGallery) {
      this.adoptedViewportFallbackKeys.add(resourceKey);
    }
    if (decoder.hls) {
      this.hlses[resourceKey] = decoder.hls;
      if (decoder.resolvedSource) {
        this.hlsSources.set(resourceKey, decoder.resolvedSource);
      }
    }
    this.removeViewportDecoderHandoffHostIfEmpty();
    return decoder.element;
  }

  /**
   * Demotes the gallery's active viewport-derived pipeline back to the exact
   * visible Media-grid scope. This is deliberately a close-only operation:
   * force-close and stale galleries still use the regular destructive path.
   */
  public returnViewportWarmDecoder(
    resourceKey: string | null | undefined,
    videoElement: HTMLVideoElement | null | undefined,
    scope: string | null | undefined,
    mediaId: string | number | null | undefined,
  ): boolean {
    if (!resourceKey || !videoElement || !scope || mediaId === null || mediaId === undefined) {
      return false;
    }
    if (!this.canReturnViewportWarmDecoder(resourceKey, videoElement, scope, mediaId)) {
      return false;
    }
    const key = `${scope}:${mediaId}`;
    const decoder = this.adoptedViewportWarmDecoders.get(resourceKey)!;
    const host = this.viewportWarmHosts.get(scope)!;

    this.adoptedViewportWarmDecoders.delete(resourceKey);
    this.adoptedViewportFallbackKeys.delete(resourceKey);
    this.detachPlayerUi(resourceKey);
    this.detachHlsEvents(resourceKey);
    const hls = this.hlses[resourceKey] || decoder.hls;
    delete this.hlses[resourceKey];
    this.hlsSources.delete(resourceKey);
    this.resourceOwners.delete(resourceKey);
    this.resourceScopes.delete(resourceKey);
    this.decoderModes.delete(resourceKey);
    this.warmBufferedVideoKeys.delete(resourceKey);
    this.bufferedAutoplayAttempts.delete(resourceKey);
    this.resolveDecoderReadinessWaiters(resourceKey, false);

    if (!this.isReturnedViewportDecoderHealthy(decoder, hls, videoElement)) {
      decoder.state = 'invalid';
      this.disposeDetachedViewportWarmDecoder(decoder);
      return false;
    }

    decoder.hls = hls;
    decoder.key = key;
    decoder.state = 'buffered';
    decoder.returnedFromGallery = true;
    decoder.element.pause();
    decoder.element.muted = true;
    decoder.element.defaultMuted = true;
    decoder.element.autoplay = true;
    try {
      decoder.element.currentTime = 0;
    } catch {
      // Metadata can still be pending for a claimed decoder; its warm event
      // path will start at zero once the source becomes seekable.
    }
    this.prepareViewportDecoderElement(decoder.element);
    host.appendChild(decoder.element);
    this.viewportWarmDecoders.set(key, decoder);
    this.bindViewportWarmDecoderEvents(decoder);
    this.warmViewportDecoderFromStart(decoder);
    return true;
  }

  public canReturnViewportWarmDecoder(
    resourceKey: string | null | undefined,
    videoElement: HTMLVideoElement | null | undefined,
    scope: string | null | undefined,
    mediaId: string | number | null | undefined,
  ): boolean {
    if (!resourceKey || !videoElement || !scope || mediaId === null || mediaId === undefined) {
      return false;
    }
    const key = `${scope}:${mediaId}`;
    const decoder = this.adoptedViewportWarmDecoders.get(resourceKey);
    return decoder?.element === videoElement
      && !!this.viewportWarmHosts.get(scope)?.isConnected
      && !!this.viewportWarmVisibleKeys.get(scope)?.has(key)
      && this.isReturnedViewportDecoderHealthy(decoder, this.hlses[resourceKey], videoElement);
  }

  /**
   * A gallery may take an in-flight decoder, but never one which has not yet
   * attached its source. This preserves one MSE pipeline for a fast tap while
   * still allowing a cold start to use the ordinary gallery fallback.
   */
  private isViewportWarmDecoderAdoptable(decoder: ViewportWarmDecoder): boolean {
    if (decoder.returnedFromGallery) {
      return decoder.state === 'buffered'
        && this.isReturnedViewportDecoderHealthy(decoder, decoder.hls, decoder.element);
    }
    return this.isViewportWarmDecoderTransferable(decoder);
  }

  private isViewportWarmDecoderTransferable(decoder: ViewportWarmDecoder): boolean {
    if (!decoder.resolvedSource || decoder.state === 'invalid') {
      return false;
    }
    if (decoder.hls) {
      const hlsMedia = (decoder.hls as Hls & {media?: HTMLMediaElement | null}).media;
      // Hls.js exposes `media` after attachMedia(). Treat an absent property
      // as attached for the small compatible test-double/native-wrapper
      // surface, but never accept an explicitly different media element.
      return !hlsMedia || hlsMedia === decoder.element;
    }
    return decoder.element.src === decoder.resolvedSource || decoder.element.currentSrc === decoder.resolvedSource;
  }

  /** A fully warmed neighbour can render immediately without keeping HLS loading. */
  private isViewportWarmDecoderTransferReady(decoder: ViewportWarmDecoder): boolean {
    return decoder.state === 'buffered'
      && !!decoder.resolvedSource
      && this.hasBufferedThrough(decoder.element, decoder.bufferTargetEnd);
  }

  /**
   * A gallery return is safe only while the very same MSE-backed element still
   * owns an initial buffer.  This intentionally rejects a half-destroyed Plyr
   * node instead of allowing it to create a permanently waiting modal.
   */
  private isReturnedViewportDecoderHealthy(
    decoder: ViewportWarmDecoder | undefined,
    hls: Hls | undefined,
    element: HTMLVideoElement,
  ): boolean {
    if (!decoder || decoder.element !== element || !element.isConnected || !decoder.resolvedSource) {
      return false;
    }
    if (!hls) {
      return element.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
        && this.hasBufferedAhead(element, 0.1);
    }
    const hlsMedia = (hls as Hls & {media?: HTMLMediaElement | null}).media;
    return hlsMedia === element
      && this.hasBufferedThrough(element, decoder.bufferTargetEnd);
  }

  /** Safe for a failed modal creation, replacement and normal close. */
  public disposeViewportDecoderHandoff(handoff: ViewportDecoderHandoff | null | undefined): void {
    if (!handoff) {
      return;
    }
    const decoder = this.viewportDecoderHandoffs.get(handoff.token);
    if (!decoder) {
      return;
    }
    this.viewportDecoderHandoffs.delete(handoff.token);
    this.disposeDetachedViewportWarmDecoder(decoder);
    this.removeViewportDecoderHandoffHostIfEmpty();
  }

  private async createViewportWarmDecoder(
    key: string,
    scope: string,
    host: HTMLElement,
    mediaObject: MediaObjectInterface,
  ): Promise<void> {
    const source = this.getPlaylistSource(mediaObject);
    if (!source || this.viewportWarmDecoders.has(key)) {
      return;
    }

    const element = document.createElement('video');
    element.muted = true;
    element.defaultMuted = true;
    element.autoplay = true;
    element.playsInline = true;
    element.preload = 'auto';
    element.setAttribute('aria-hidden', 'true');
    element.tabIndex = -1;
    // Keep the media element attached (Safari will otherwise aggressively
    // suspend it), but make it non-rendering and non-interactive.  This is a
    // decoder pool, never a hidden Timeline card.
    Object.assign(element.style, {
      position: 'absolute', width: '1px', height: '1px', opacity: '0',
      pointerEvents: 'none', left: '-10000px', top: '0',
    });
    host.appendChild(element);

    let resolveSourceReady: (ready: boolean) => void = () => undefined;
    const sourceReady = new Promise<boolean>((resolve) => {
      resolveSourceReady = resolve;
    });
    let resolveBufferedReady: (ready: boolean) => void = () => undefined;
    const bufferedReady = new Promise<boolean>((resolve) => {
      resolveBufferedReady = resolve;
    });
    const decoder: ViewportWarmDecoder = {
      key,
      scope,
      element,
      state: 'pending',
      sourceReady,
      resolveSourceReady,
      bufferedReady,
      resolveBufferedReady,
      bufferTargetEnd: this.warmBufferTargetSeconds,
    };
    this.viewportWarmDecoders.set(key, decoder);
    const resolvedSource = await this.mediaOffline.resolveUrl(source, 'playlist');
    if (!this.isViewportWarmDecoderLive(decoder) || !resolvedSource) {
      this.disposeAnyViewportWarmDecoder(decoder);
      return;
    }
    decoder.resolvedSource = resolvedSource;

    if (this.shouldUseNativeHls(element) || !Hls.isSupported()) {
      element.src = resolvedSource;
      element.load();
      const markNativePipelineReady = () => {
        if (!this.isViewportWarmDecoderLive(decoder)) {
          return;
        }
        decoder.resolveSourceReady(true);
      };
      element.addEventListener('loadedmetadata', markNativePipelineReady, {once: true});
      element.addEventListener('error', () => decoder.resolveSourceReady(false), {once: true});
      this.warmViewportDecoderFromStart(decoder);
      // Native HLS has no stopLoading API.  Releasing this decoder as soon as
      // the tile leaves the real viewport is still deterministic.
      return;
    }

    const hls = new Hls({
      // Match the regular Timeline lifecycle. With `autoStartLoad: false`,
      // a startLoad() issued while Hls.js was still attaching the hidden
      // element could be deferred until the gallery promoted that decoder.
      // The visible grid must begin fetching as soon as its manifest parses;
      // the four-second limits below provide the memory back-pressure.
      autoStartLoad: true,
      startLevel: 0,
      maxBufferLength: this.warmBufferTargetSeconds,
      maxMaxBufferLength: this.warmBufferTargetSeconds,
      liveDurationInfinity: true,
      capLevelToPlayerSize: true,
      testBandwidth: false,
    });
    decoder.hls = hls;
    this.bindViewportWarmDecoderEvents(decoder);
    hls.attachMedia(element);
    hls.loadSource(resolvedSource);
    // This is intentionally also explicit. `autoStartLoad` handles the
    // manifest-parsed path; this covers HLS instances that have already
    // attached by the time the source is assigned.
    this.warmViewportDecoderFromStart(decoder, 0);
  }

  private tryStartMutedWarmPlayback(element: HTMLVideoElement): void {
    try {
      const playback = element.play();
      if (playback && typeof (playback as Promise<void>).catch === 'function') {
        void (playback as Promise<void>).catch(() => undefined);
      }
    } catch {
      // Some engines defer muted playback for an off-screen media element.
      // HLS can still fill its bounded buffer without this optimisation.
    }
  }

  private disposeViewportWarmDecoder(key: string): void {
    const decoder = this.viewportWarmDecoders.get(key);
    if (!decoder) {
      return;
    }
    this.viewportWarmDecoders.delete(key);
    this.disposeDetachedViewportWarmDecoder(decoder);
  }

  private disposeAnyViewportWarmDecoder(decoder: ViewportWarmDecoder): void {
    const poolDecoder = this.viewportWarmDecoders.get(decoder.key);
    if (poolDecoder === decoder) {
      this.viewportWarmDecoders.delete(decoder.key);
    }
    [...this.viewportDecoderHandoffs.entries()]
      .filter(([, candidate]) => candidate === decoder)
      .forEach(([token]) => this.viewportDecoderHandoffs.delete(token));
    [...this.adoptedViewportWarmDecoders.entries()]
      .filter(([, candidate]) => candidate === decoder)
      .forEach(([resourceKey]) => this.adoptedViewportWarmDecoders.delete(resourceKey));
    decoder.resolveSourceReady(false);
    this.disposeDetachedViewportWarmDecoder(decoder);
  }

  private disposeDetachedViewportWarmDecoder(decoder: ViewportWarmDecoder): void {
    decoder.state = 'invalid';
    decoder.resolveSourceReady(false);
    decoder.resolveBufferedReady(false);
    decoder.nativeWarmCleanup?.();
    decoder.nativeWarmCleanup = undefined;
    if (decoder.stopTimer) {
      clearTimeout(decoder.stopTimer);
    }
    try {
      this.stopHlsLoading(decoder.hls);
      decoder.hls?.detachMedia();
      decoder.hlsListenerCleanup?.();
      decoder.hlsListenerCleanup = undefined;
      decoder.hls?.destroy();
    } catch {
      // HLS may already have performed its fatal-error cleanup.
    }
    this.clearNativeVideoElement(decoder.element);
    decoder.element.remove();
  }

  private isViewportWarmDecoderLive(decoder: ViewportWarmDecoder): boolean {
    return this.viewportWarmDecoders.get(decoder.key) === decoder
      || [...this.viewportDecoderHandoffs.values()].some((candidate) => candidate === decoder)
      || [...this.adoptedViewportWarmDecoders.values()].some((candidate) => candidate === decoder);
  }

  private bindViewportWarmDecoderEvents(decoder: ViewportWarmDecoder): void {
    const hls = decoder.hls;
    if (!hls) {
      return;
    }
    decoder.hlsListenerCleanup?.();
    const manifestParsed = () => {
      if (!this.isViewportWarmDecoderLive(decoder)) {
        return;
      }
      decoder.resolveSourceReady(true);
      if (!this.isViewportWarmDecoderPoolOwned(decoder)) {
        return;
      }
      this.tryStartMutedWarmPlayback(decoder.element);
    };
    const fragBuffered = () => {
      if (!this.isViewportWarmDecoderLive(decoder) || !this.hasBufferedThrough(decoder.element, decoder.bufferTargetEnd)) {
        return;
      }
      // A claimed decoder can finish buffering after the modal has mounted.
      // Its state remains `adopted`, but its readiness capability records that
      // the original MSE now owns a real contiguous startup range.
      decoder.resolveBufferedReady(true);
      if (!this.isViewportWarmDecoderPoolOwned(decoder)) {
        return;
      }
      decoder.state = 'buffered';
      decoder.element.pause();
    };
    const error = (_event: string, data: any) => {
      if (data.fatal) {
        decoder.state = 'invalid';
        decoder.resolveSourceReady(false);
        decoder.resolveBufferedReady(false);
        if (this.isViewportWarmDecoderPoolOwned(decoder)) {
          this.disposeAnyViewportWarmDecoder(decoder);
        }
      }
    };
    hls.on(Hls.Events.MANIFEST_PARSED, manifestParsed);
    hls.on(Hls.Events.FRAG_BUFFERED, fragBuffered);
    hls.on(Hls.Events.ERROR, error);
    decoder.hlsListenerCleanup = () => {
      hls.off(Hls.Events.MANIFEST_PARSED, manifestParsed);
      hls.off(Hls.Events.FRAG_BUFFERED, fragBuffered);
      hls.off(Hls.Events.ERROR, error);
    };
  }

  private isViewportWarmDecoderPoolOwned(decoder: ViewportWarmDecoder): boolean {
    return this.viewportWarmDecoders.get(decoder.key) === decoder
      || [...this.viewportDecoderHandoffs.values()].some((candidate) => candidate === decoder);
  }

  private warmViewportDecoderFromStart(decoder: ViewportWarmDecoder, startPosition?: number): void {
    if (!decoder.hls) {
      decoder.nativeWarmCleanup?.();
      if (decoder.stopTimer) {
        clearTimeout(decoder.stopTimer);
        decoder.stopTimer = undefined;
      }
      let stopWhenBuffered: () => void = () => undefined;
      const stopNativeWarmup = () => {
        if (decoder.element.currentTime >= decoder.bufferTargetEnd) {
          decoder.state = 'buffered';
          decoder.resolveBufferedReady(true);
        }
        decoder.element.pause();
        decoder.element.removeEventListener('timeupdate', stopWhenBuffered);
        if (decoder.stopTimer) {
          clearTimeout(decoder.stopTimer);
          decoder.stopTimer = undefined;
        }
        decoder.nativeWarmCleanup = undefined;
      };
      stopWhenBuffered = () => {
        if (decoder.element.currentTime >= decoder.bufferTargetEnd) {
          stopNativeWarmup();
        }
      };
      decoder.element.addEventListener('timeupdate', stopWhenBuffered);
      decoder.nativeWarmCleanup = () => {
        decoder.element.removeEventListener('timeupdate', stopWhenBuffered);
      };
      decoder.stopTimer = setTimeout(stopNativeWarmup, 8000);
      this.tryStartMutedWarmPlayback(decoder.element);
      return;
    }
    if (this.hasBufferedThrough(decoder.element, decoder.bufferTargetEnd)) {
      decoder.state = 'buffered';
      decoder.resolveBufferedReady(true);
      decoder.element.pause();
      return;
    }
    this.startHlsLoading(decoder.hls, startPosition);
    this.tryStartMutedWarmPlayback(decoder.element);
  }

  private prepareViewportDecoderElement(element: HTMLVideoElement): void {
    element.setAttribute('aria-hidden', 'true');
    element.tabIndex = -1;
    Object.assign(element.style, {
      position: 'absolute', width: '1px', height: '1px', opacity: '0',
      pointerEvents: 'none', left: '-10000px', top: '0',
    });
  }

  private getViewportWarmHost(scope: string, host: HTMLElement): HTMLElement {
    const existing = this.viewportWarmHosts.get(scope);
    if (existing?.isConnected) {
      return existing;
    }
    const poolHost = document.createElement('div');
    poolHost.setAttribute('aria-hidden', 'true');
    poolHost.dataset.viewportWarmDecoderScope = scope;
    Object.assign(poolHost.style, {position: 'absolute', width: '0', height: '0', overflow: 'hidden'});
    host.appendChild(poolHost);
    this.viewportWarmHosts.set(scope, poolHost);
    return poolHost;
  }

  private removeViewportWarmHost(scope: string): void {
    const host = this.viewportWarmHosts.get(scope);
    host?.remove();
    this.viewportWarmHosts.delete(scope);
  }

  private getViewportDecoderHandoffHost(): HTMLElement {
    if (this.viewportDecoderHandoffHost?.isConnected) {
      return this.viewportDecoderHandoffHost;
    }
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.dataset.viewportDecoderHandoff = 'true';
    Object.assign(host.style, {position: 'absolute', width: '0', height: '0', overflow: 'hidden'});
    document.body.appendChild(host);
    this.viewportDecoderHandoffHost = host;
    return host;
  }

  private removeViewportDecoderHandoffHostIfEmpty(): void {
    if (this.viewportDecoderHandoffs.size || !this.viewportDecoderHandoffHost) {
      return;
    }
    this.viewportDecoderHandoffHost.remove();
    this.viewportDecoderHandoffHost = null;
  }

  private restoreViewportDecoderElement(element: HTMLVideoElement, templateVideo: HTMLVideoElement): void {
    element.removeAttribute('aria-hidden');
    element.tabIndex = templateVideo.tabIndex;
    element.id = templateVideo.id;
    element.className = templateVideo.className;
    element.style.cssText = templateVideo.style.cssText;
    for (const attribute of Array.from(templateVideo.attributes)) {
      if (attribute.name !== 'id' && attribute.name !== 'class' && attribute.name !== 'style' && attribute.name !== 'src') {
        element.setAttribute(attribute.name, attribute.value);
      }
    }
  }

  private getPlaylistSource(mediaObject: MediaObjectInterface | null | undefined): string | null {
    return mediaObject?.hyperlinks?.find((link) => link?.name === 'm3u8')?.uri || null;
  }

  set muted(value: boolean) {
    this.setMuted(value);
  }

  async initPlayer(
    videoElement: HTMLVideoElement,
    mediaObject: MediaObjectInterface,
    callback?: () => void,
    isStillActive: () => boolean = () => true,
    options: VideoPlayerOptions = {}
  ): Promise<Plyr | null> {
    this.startedPlayerInitializations += 1;
    this.activePlayerInitializations += 1;

    try {
      return await this.createPlayer(videoElement, mediaObject, callback, isStillActive, options);
    } finally {
      this.activePlayerInitializations = Math.max(0, this.activePlayerInitializations - 1);
    }
  }

  private async createPlayer(
    videoElement: HTMLVideoElement,
    mediaObject: MediaObjectInterface,
    callback: (() => void) | undefined,
    isStillActive: () => boolean,
    options: VideoPlayerOptions
  ): Promise<Plyr | null> {
    const hyperlinks = Array.isArray(mediaObject.hyperlinks) ? mediaObject.hyperlinks : [];
    const m3u8 = hyperlinks.find(e => e.name === 'm3u8');
    const original = hyperlinks.find((e) => e.name === 'original');
    const videoKey = options.resourceKey || videoElement.id;
    let adoptedDecoder = this.adoptedViewportWarmDecoders.get(videoKey);
    let adoptedPipeline = false;
    let resolvedM3u8: string | null = null;
    if (adoptedDecoder) {
      // Do not turn a manifest event into a second activation gate.  The
      // claimed element is already attached to its HLS instance; Plyr can be
      // created while that pipeline fills and native `play()` will queue on
      // the same element.  Waiting here used to make a cache hit look cold.
      if (adoptedDecoder.state !== 'invalid' && adoptedDecoder.resolvedSource) {
        resolvedM3u8 = adoptedDecoder.resolvedSource;
        adoptedPipeline = true;
        if (adoptedDecoder.hls) {
          this.hlses[videoKey] = adoptedDecoder.hls;
          this.hlsSources.set(videoKey, adoptedDecoder.resolvedSource);
        }
      } else {
        // The viewport task was cancelled before it reached a usable source.
        // Fall back to the regular gallery path rather than retaining a dead
        // handoff capability.
        this.adoptedViewportWarmDecoders.delete(videoKey);
        this.adoptedViewportFallbackKeys.delete(videoKey);
        this.destroyVideoResources(videoKey, videoElement);
        adoptedDecoder = undefined;
      }
    }
    if (!resolvedM3u8) {
      resolvedM3u8 = m3u8?.uri ? await this.mediaOffline.resolveUrl(m3u8.uri, 'playlist') : null;
    }
    const resolvedOriginal = adoptedPipeline ? null : (original?.uri ? await this.mediaOffline.resolveUrl(original.uri, 'video') : null);
    if (!isStillActive()) {
      return this.cancelPlayerInitialization();
    }
    const mode = options.mode || 'active';
    // Start the media pipeline before the optional Plyr UI module resolves.
    // The first Reel used to wait for that dynamic import before fetching even
    // its manifest; following videos were fast only because the module was
    // already in memory. This remains the same single active media element.
    videoElement.muted = this.muted;
    videoElement.defaultMuted = true;
    videoElement.autoplay = mode === 'active';
    let hls = this.hlses[videoKey];
    // `adoptViewportWarmDecoder()` has already moved this exact MediaSource
    // and element under the gallery resource key. Reattaching HLS here would
    // discard the buffered seconds we are deliberately preserving.
    adoptedPipeline = adoptedPipeline || (this.resourceOwners.get(videoKey) === videoElement
      && (!!hls || !!(videoElement.currentSrc || videoElement.src)));
    let hlsCreated = false;
    this.resourceOwners.set(videoKey, videoElement);
    if (options.scope) {
      this.resourceScopes.set(videoKey, options.scope);
    }
    if (options.cacheForOfflineOnPlay !== false) {
      this.cachePlayedMediaForOffline(videoElement, m3u8?.uri || original?.uri || null, m3u8?.uri ? 'playlist' : 'video');
    }

    if (resolvedM3u8 && this.shouldUseNativeHls(videoElement) && !adoptedPipeline) {
      videoElement.src = resolvedM3u8;
      videoElement.load();
    } else if (Hls.isSupported() && resolvedM3u8) {
      const isDesktopViewport = (window.innerWidth || 0) >= 1024;
      if (!hls) {
        hls = new Hls({
          // Every prepared neighbour keeps a bounded, valid HLS pipeline.
          // It is paused by the scheduler, rather than being cut off after a
          // single segment and later rebuilt during autoplay.
          // Mounted Timeline cards deliberately use the stable pre-#400 HLS
          // lifecycle: source attachment starts immediately and stays intact
          // until the card is actually removed.
          autoStartLoad: true,
          // A gallery keeps only the current decoder plus one forward warm
          // neighbour. The latter needs the startup range, not a full second
          // player buffer; keeping it at four seconds avoids trading the
          // transition gain for Chrome RAM pressure.
          maxBufferLength: mode === 'warm' ? this.warmBufferTargetSeconds : 8,
          maxMaxBufferLength: mode === 'warm' ? this.warmBufferTargetSeconds : 16,
          liveDurationInfinity: true,
          capLevelToPlayerSize: true,
          abrEwmaDefaultEstimate: isDesktopViewport ? 12_000_000 : 6_000_000,
          minAutoBitrate: isDesktopViewport ? 2_000_000 : 1_000_000,
          testBandwidth: false,
          // Startup prewarm stores HLS level 0's manifest, init data and first
          // fragment. Start from the same deterministic
          // level so a just-opened Media/Reel can consume that work instead
          // of choosing an unrelated ABR rendition and fetching again. HLS
          // still switches up normally after the first fragment.
          startLevel: 0,
        });
        hlsCreated = true;
      }
      if (!isStillActive()) {
        this.destroyUntrackedHls(hls, hlsCreated);
        this.releaseVideoDecoder(videoKey, videoElement);
        return this.cancelPlayerInitialization();
      }

      const hlsForVideo = hls;
      this.hlses[videoKey] = hlsForVideo;
      this.hlsSources.set(videoKey, resolvedM3u8);
      // Keep a claimed viewport decoder warm until the gallery promotes it;
      // its attached HLS instance continues to enforce the bounded buffer.
      this.decoderModes.set(videoKey, mode);
      this.bufferedAutoplayAttempts.delete(videoKey);

      this.bindHlsEvents(videoKey, videoElement, hlsForVideo);
      if (!adoptedPipeline) {
        hlsForVideo.attachMedia(videoElement);
        hlsForVideo.loadSource(resolvedM3u8);
      }

    } else if (resolvedOriginal) {
      videoElement.src = resolvedOriginal;
      videoElement.load();
    }

    if (options.startPlaybackImmediately) {
      this.startNativePlayback(videoElement);
    }

    if (!isStillActive()) {
      this.releaseVideoDecoder(videoKey, videoElement);
      return this.cancelPlayerInitialization();
    }

    await this.loadPlyrModule();
    if (!isStillActive()) {
      this.releaseVideoDecoder(videoKey, videoElement);
      return this.cancelPlayerInitialization();
    }

    const Plyr = this.plyrModule?.default || this.plyrModule;
    let player = this.players[videoKey];
    let playerCreated = false;
    if (!player) {
      player = new Plyr(videoElement, {
        controls: [],
        muted: true,
        clickToPlay: false,
        enabled: true
      });
      playerCreated = true;
    }

    if (!isStillActive()) {
      this.destroyUntrackedPlayer(player, playerCreated);
      this.releaseVideoDecoder(videoKey, videoElement);
      return this.cancelPlayerInitialization();
    }

    this.players[videoKey] = player;
    player.muted = this.muted;
    const playerReady = () => {
      callback?.();
      this.notifyAutoplayReady(videoKey, videoElement);
    };
    this.eventListeners.push({key: videoKey, value: {key: 'ready', value: playerReady}});
    player.on('ready', playerReady);

    return player;
  }

  private bindHlsEvents(videoKey: string, videoElement: HTMLVideoElement, hls: Hls): void {
    this.detachHlsEvents(videoKey);
    const manifestParsed = () => {
      if (this.hlses[videoKey] !== hls || this.resourceOwners.get(videoKey) !== videoElement) {
        return;
      }
      this.notifyAutoplayReady(videoKey, videoElement);
    };
    const fragBuffered = () => this.handleBufferedFragment(videoKey, videoElement, hls);
    const error = (_event: string, data: any) => {
      this.hlsErrors += 1;
      // Do not log the Hls error object itself. DevTools retains logged
      // objects and that object can reference the media element/decoder.
      console.warn('HLS.js error', data.type, data.details, data.fatal);
      if (!data.fatal) {
        return;
      }
      if (this.adoptedViewportFallbackKeys.has(videoKey)) {
        // A returned decoder already had a playable startup buffer. A fatal
        // HLS error therefore means its MSE cannot safely be reused; recover
        // once through the ordinary gallery source instead of spinning.
        this.adoptedViewportFallbackKeys.delete(videoKey);
        this.failedViewportDecoderFallbackKeys.add(videoKey);
        this.destroyVideoResources(videoKey, videoElement);
        return;
      }
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          this.startHlsLoading(hls);
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;
        default:
          this.disposeVideo(videoKey, videoElement);
          break;
      }
    };
    hls.on(Hls.Events.MANIFEST_PARSED, manifestParsed);
    hls.on(Hls.Events.FRAG_BUFFERED, fragBuffered);
    hls.on(Hls.Events.ERROR, error);
    this.hlsEventListeners.set(videoKey, {hls, manifestParsed, fragBuffered, error});
  }

  private detachHlsEvents(videoKey: string): void {
    const listeners = this.hlsEventListeners.get(videoKey);
    if (!listeners) {
      return;
    }
    listeners.hls.off(Hls.Events.MANIFEST_PARSED, listeners.manifestParsed);
    listeners.hls.off(Hls.Events.FRAG_BUFFERED, listeners.fragBuffered);
    listeners.hls.off(Hls.Events.ERROR, listeners.error);
    this.hlsEventListeners.delete(videoKey);
  }

  private cancelPlayerInitialization(): null {
    this.cancelledPlayerInitializations += 1;
    return null;
  }

  private destroyUntrackedPlayer(player: Plyr | undefined, shouldDestroy: boolean): void {
    if (!player || !shouldDestroy) {
      return;
    }
    try {
      player.destroy();
    } catch {
      // The player may already have been detached by a synchronously destroyed card.
    }
  }

  private destroyUntrackedHls(hls: Hls | undefined, shouldDestroy: boolean): void {
    if (!hls || !shouldDestroy) {
      return;
    }
    try {
      hls.detachMedia();
      hls.destroy();
    } catch {
      // HLS cleanup is idempotent from the component perspective.
    }
  }

  private cachePlayedMediaForOffline(
    videoElement: HTMLVideoElement,
    sourceUrl: string | null,
    type: 'playlist' | 'video'
  ): void {
    if (!sourceUrl || this.offlineCachingBoundVideos.has(videoElement)) {
      return;
    }
    this.offlineCachingBoundVideos.add(videoElement);
    videoElement.addEventListener('play', () => this.mediaOffline.cacheForOffline(sourceUrl, type), {once: true});
  }

  /**
   * Arms disk persistence immediately before user-visible gallery playback.
   * Gallery neighbours call muted `play()` only to warm their MSE and must not
   * be mistaken for a user selection.
   */
  public cacheMediaForOfflineOnPlay(videoElement: HTMLVideoElement, mediaObject: MediaObjectInterface): void {
    const playlistUrl = this.getPlaylistSource(mediaObject);
    const originalUrl = mediaObject?.hyperlinks?.find((link) => link?.name === 'original')?.uri || null;
    this.cachePlayedMediaForOffline(videoElement, playlistUrl || originalUrl, playlistUrl ? 'playlist' : 'video');
  }

  public registerTimelineVideo(
    videoElement: HTMLVideoElement,
    onDecoderReleased?: () => void,
  ): void {
    if (!this.isHtmlVideoElement(videoElement) || !videoElement.id) {
      return;
    }

    const existing = this.timelineVideos.get(videoElement.id);
    if (existing && existing.element !== videoElement) {
      // Virtual scrolling can mount a replacement before Angular destroys the
      // old card. Stop observing that node immediately so it cannot retain a
      // stale callback or compete for the active decoder window.
      this.intersectionObserver?.unobserve(existing.element);
      existing.element.pause();
      this.cancelPendingAutoplay(existing);
      // Invalidate an in-flight initializer before its card is destroyed. A
      // virtualized replacement can otherwise finish later and overwrite the
      // decoder belonging to the new DOM node with the same media id.
      this.releaseVideoDecoder(existing.id, existing.element);
      existing.onDecoderReleased?.();
      if (this.currentVideoId === existing.id) {
        this.currentVideoId = null;
      }
    }

    this.timelineVideos.set(videoElement.id, {
      id: videoElement.id,
      element: videoElement,
      onDecoderReleased,
      autoplayAttempt: 0,
      autoplayState: 'idle',
    });
    videoElement.muted = this.muted;

    this.ensureIntersectionObserver();
    this.ensureViewportListeners();
    this.intersectionObserver?.observe(videoElement);
    this.scheduleViewportEvaluation();
  }

  public unregisterTimelineVideo(videoKey: string | null | undefined, owner?: HTMLVideoElement | null): boolean {
    if (!videoKey) {
      return false;
    }

    const registered = this.timelineVideos.get(videoKey);
    let released = false;
    if (registered && (!owner || registered.element === owner)) {
      this.intersectionObserver?.unobserve(registered.element);
      this.cancelPendingAutoplay(registered);
      this.releaseTimelineDecoder(registered);
      released = true;
      if (this.currentVideoId === videoKey) {
        registered.element.pause();
        this.currentVideoId = null;
      }
      this.timelineVideos.delete(videoKey);
    }

    if (!this.timelineVideos.size) {
      this.destroyViewportListeners();
      this.destroyIntersectionObserver();
      this.cancelViewportEvaluation();
    } else {
      this.scheduleViewportEvaluation();
    }
    return released;
  }

  public setAutoplaySuppressed(suppressed: boolean): void {
    if (this.autoplaySuppressed === suppressed) {
      return;
    }

    this.autoplaySuppressed = suppressed;
    this.scheduleViewportEvaluation();
  }

  videoScroll(): void {
    this.scheduleViewportEvaluation();
  }

  public scheduleViewportEvaluation(): void {
    if (!this.isBrowserEnvironment()) {
      return;
    }

    if (this.animationFrameId !== null) {
      return;
    }

    if (typeof requestAnimationFrame !== 'function') {
      this.evaluateViewport();
      return;
    }

    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = null;
      this.evaluateViewport();
    });
  }

  public setMuted(muted: boolean): void {
    if (this.mutedSubject.value === muted) {
      this.syncMuteState(muted);
      this.updateVolumeButtonsWatch(muted);
      return;
    }

    this.mutedSubject.next(muted);
    this.syncMuteState(muted);
    this.updateVolumeButtonsWatch(muted);
  }

  public pauseAllExcept(id: number, prefix: string = 'video') {
    const keepId = `${prefix}-${id}`;
    this.timelineVideos.forEach(({element, id: registeredId}) => {
      if (registeredId === keepId) {
        return;
      }
      element.pause();
      if (this.currentVideoId === registeredId) {
        this.currentVideoId = null;
      }
    });
  }

  /** Releases every runtime resource owned by one media element. Safe to call repeatedly. */
  public disposeVideo(videoKey: string | null | undefined, videoElement?: HTMLVideoElement | null): void {
    if (!videoKey) {
      this.clearNativeVideoElement(videoElement);
      return;
    }
    const releasedByUnregister = this.unregisterTimelineVideo(videoKey, videoElement);
    if (!releasedByUnregister) {
      this.destroyVideoResources(videoKey, videoElement);
    }
  }

  /** Explicitly releases every player created by a transient owner such as a gallery modal. */
  public disposeScope(scope: string | null | undefined): void {
    if (!scope) {
      return;
    }
    [...this.resourceScopes.entries()]
      .filter(([, resourceScope]) => resourceScope === scope)
      .forEach(([videoKey]) => this.disposeVideo(videoKey, this.resourceOwners.get(videoKey)));
  }

  /** Releases HLS/Plyr without removing the lightweight viewport candidate. */
  public releaseVideoDecoder(videoKey: string | null | undefined, videoElement?: HTMLVideoElement | null): void {
    if (!videoKey) {
      this.clearNativeVideoElement(videoElement);
      return;
    }
    const registered = this.timelineVideos.get(videoKey);
    if (registered && (!videoElement || registered.element === videoElement)) {
      this.cancelPendingAutoplay(registered);
    }
    this.destroyVideoResources(videoKey, videoElement);
  }

  /** Updates a transient gallery video's active state without touching Timeline cards. */
  public setVideoDecoderMode(
    videoKey: string | null | undefined,
    videoElement: HTMLVideoElement | null | undefined,
    mode: VideoDecoderMode
  ): void {
    if (!videoKey) {
      return;
    }
    if (videoElement) {
      videoElement.muted = this.muted;
      videoElement.defaultMuted = true;
      videoElement.autoplay = mode === 'active';
    }
    if (mode === 'inactive') {
      this.releaseVideoDecoder(videoKey, videoElement);
      return;
    }
    // Native HLS and original-file fallback have no Hls.js instance, but they
    // still need the same active/warm lifecycle for the readiness gate.
    if (!this.hlses[videoKey]) {
      this.decoderModes.set(videoKey, mode);
      if (mode !== 'active') {
        this.resolveDecoderReadinessWaiters(videoKey, false);
      }
      return;
    }
    if (mode !== 'active') {
      this.resolveDecoderReadinessWaiters(videoKey, false);
    }
    if (videoElement) {
      this.resumeHlsDecoder(videoKey, videoElement, mode === 'active');
    }
  }

  /**
   * Promotes a prepared gallery decoder and resolves only once the native
   * element can actually start.  A caller-owned generation guard decides
   * whether the resolved attempt is still the selected Swiper slide.
   */
  public activateVideoDecoder(
    videoKey: string | null | undefined,
    videoElement: HTMLVideoElement | null | undefined,
  ): Promise<boolean> {
    if (!videoKey || !videoElement) {
      return Promise.resolve(false);
    }
    this.setVideoDecoderMode(videoKey, videoElement, 'active');
    const readiness = this.waitForDecoderReadiness(videoKey, videoElement);
    if (!this.adoptedViewportFallbackKeys.has(videoKey)) {
      return readiness;
    }
    return this.waitForReturnedViewportDecoderReadiness(videoKey, videoElement, readiness);
  }

  /** True when the existing native/MSE pipeline can render without resuming HLS. */
  public isVideoDecoderPlaybackReady(videoKey: string | null | undefined, videoElement: HTMLVideoElement | null | undefined): boolean {
    return !!videoKey
      && !!videoElement
      && this.resourceOwners.get(videoKey) === videoElement
      && this.isPlaybackReady(videoKey, videoElement);
  }

  /** A bounded warm decoder resumes network work only after native playback actually stalls. */
  public resumeVideoDecoderAfterWaiting(videoKey: string | null | undefined, videoElement: HTMLVideoElement | null | undefined): void {
    if (!videoKey || !videoElement || this.resourceOwners.get(videoKey) !== videoElement || this.decoderModes.get(videoKey) !== 'active') {
      return;
    }
    const hls = this.hlses[videoKey];
    if (!hls || !this.hlsSources.has(videoKey)) {
      return;
    }
    // HLS normally resumes itself as the bounded buffer drains. This call is
    // an idempotent safety net for engines that emit `waiting` first.
    if (!this.warmBufferedVideoKeys.delete(videoKey)) {
      return;
    }
    this.startHlsLoading(hls);
  }

  /** Consumed by MediaView to run exactly one ordinary, cold gallery fallback. */
  public consumeViewportDecoderFallback(videoKey: string, videoElement: HTMLVideoElement): boolean {
    if (!this.failedViewportDecoderFallbackKeys.has(videoKey)) {
      return false;
    }
    this.failedViewportDecoderFallbackKeys.delete(videoKey);
    return !this.resourceOwners.has(videoKey) || this.resourceOwners.get(videoKey) === videoElement;
  }

  private waitForReturnedViewportDecoderReadiness(
    videoKey: string,
    videoElement: HTMLVideoElement,
    readiness: Promise<boolean>,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (ready: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeout);
        resolve(ready);
      };
      const timeout = setTimeout(() => {
        if (this.isPlaybackReady(videoKey, videoElement)) {
          settle(true);
          return;
        }
        // This branch applies only to an already buffered decoder returned by
        // a prior gallery. A healthy one is immediately playable; keeping an
        // invalid MSE forever is worse than one normal source reinitialisation.
        this.adoptedViewportFallbackKeys.delete(videoKey);
        this.failedViewportDecoderFallbackKeys.add(videoKey);
        this.destroyVideoResources(videoKey, videoElement);
        settle(false);
      }, 1200);
      void readiness.then(settle);
    });
  }

  /** Destroys a decoder while retaining the lightweight viewport registration. */
  private destroyVideoResources(videoKey: string, videoElement?: HTMLVideoElement | null): void {
    const owner = this.resourceOwners.get(videoKey);
    if (owner && videoElement && owner !== videoElement) {
      // A recycled virtual card may use the same media id. The old instance
      // must only clear its own native element, never the new owner's decoder.
      this.clearNativeVideoElement(videoElement);
      return;
    }

    this.adoptedViewportWarmDecoders.delete(videoKey);
    this.adoptedViewportFallbackKeys.delete(videoKey);
    this.detachHlsEvents(videoKey);
    const hls = this.hlses[videoKey];
    try {
      hls?.detachMedia();
      hls?.destroy();
    } catch {
      // A previous cleanup can race with HLS' own fatal-error teardown.
    }
    delete this.hlses[videoKey];
    this.hlsSources.delete(videoKey);
    this.decoderModes.delete(videoKey);
    this.bufferedAutoplayAttempts.delete(videoKey);
    this.warmBufferedVideoKeys.delete(videoKey);
    this.clearWarmDecoderPrime(videoKey);
    this.resolveDecoderReadinessWaiters(videoKey, false);

    this.eventListeners = this.eventListeners.filter((listener) => {
      if (listener.key !== videoKey) {
        return true;
      }
      try {
        this.players[videoKey]?.off(listener.value.key, listener.value.value);
      } catch {
        // Plyr may have already detached its event emitter.
      }
      return false;
    });

    try {
      this.players[videoKey]?.destroy();
    } catch {
      // Player destruction is intentionally best-effort and idempotent.
    }
    delete this.players[videoKey];
    this.resourceOwners.delete(videoKey);
    this.resourceScopes.delete(videoKey);
    this.releasedDecoders += 1;
    this.clearNativeVideoElement(videoElement);
  }

  /** Removes gallery UI/listeners while deliberately preserving HLS + MSE. */
  private detachPlayerUi(videoKey: string): void {
    this.eventListeners = this.eventListeners.filter((listener) => {
      if (listener.key !== videoKey) {
        return true;
      }
      try {
        this.players[videoKey]?.off(listener.value.key, listener.value.value);
      } catch {
        // Plyr may already have detached its event emitter.
      }
      return false;
    });
    try {
      this.players[videoKey]?.destroy();
    } catch {
      // The player can already be gone during a normal modal close.
    }
    delete this.players[videoKey];
  }

  private clearNativeVideoElement(videoElement: HTMLVideoElement | null | undefined): void {
    if (!videoElement) {
      return;
    }
    try {
      videoElement.pause();
      videoElement.autoplay = false;
      videoElement.removeAttribute('src');
      videoElement.src = '';
      videoElement.srcObject = null;
      videoElement.load();
    } catch {
      // Detached native media nodes do not always accept load() on all browsers.
    }
  }

  private shouldUseNativeHls(videoElement: HTMLVideoElement): boolean {
    if (!this.isHtmlVideoElement(videoElement)) {
      return false;
    }

    if (this.isNativeIos) {
      return true;
    }
    return videoElement.canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  private isHtmlVideoElement(videoElement: HTMLVideoElement | null | undefined): videoElement is HTMLVideoElement {
    return !!videoElement
      && videoElement.tagName?.toLowerCase() === 'video'
      && typeof videoElement.canPlayType === 'function';
  }

  private evaluateViewport(): void {
    this.lastScrollDirection = this.getScrollDirection();

    if (!this.timelineVideos.size) {
      this.currentVideoId = null;
      return;
    }

    if (this.autoplaySuppressed) {
      this.pauseCurrentVideo();
      return;
    }

    const candidate = this.resolveActiveVideoCandidate();
    this.syncActiveVideo(candidate?.id || null);
  }

  private resolveActiveVideoCandidate(): RegisteredTimelineVideo | null {
    const entries = this.getTrackedVideos();
    if (!entries.length) {
      return null;
    }

    const viewportMid = this.getViewportMidpoint();
    const strictCandidates = entries.filter(({element}) => this.isCenterLineInsideRect(element.getBoundingClientRect(), viewportMid));

    if (!strictCandidates.length) {
      const currentEntry = this.currentVideoId ? this.timelineVideos.get(this.currentVideoId) : null;
      if (currentEntry && this.isCenterLineInsideRect(currentEntry.element.getBoundingClientRect(), viewportMid, this.centerLineHysteresisPx)) {
        return currentEntry;
      }

      // The first Timeline card can be clearly visible below fixed app chrome
      // without intersecting the browser midpoint. In that case choose the
      // most visible mounted video instead of leaving it without a decoder.
      const viewportHeight = this.getViewportHeight();
      return entries
        .map((entry) => ({entry, rect: entry.element.getBoundingClientRect()}))
        .filter(({rect}) => rect.bottom > 0 && rect.top < viewportHeight)
        .sort((left, right) => {
          const leftVisible = Math.max(0, Math.min(left.rect.bottom, viewportHeight) - Math.max(left.rect.top, 0));
          const rightVisible = Math.max(0, Math.min(right.rect.bottom, viewportHeight) - Math.max(right.rect.top, 0));
          if (rightVisible !== leftVisible) {
            return rightVisible - leftVisible;
          }
          const leftDistance = Math.abs((left.rect.top + left.rect.bottom) / 2 - viewportMid);
          const rightDistance = Math.abs((right.rect.top + right.rect.bottom) / 2 - viewportMid);
          return leftDistance - rightDistance;
        })[0]?.entry || null;
    }

    if (strictCandidates.length === 1) {
      return strictCandidates[0];
    }

    const sorted = [...strictCandidates].sort((left, right) => {
      const leftRect = left.element.getBoundingClientRect();
      const rightRect = right.element.getBoundingClientRect();
      if (this.lastScrollDirection === 'down') {
        return rightRect.top - leftRect.top;
      }
      return leftRect.bottom - rightRect.bottom;
    });

    const currentEntry = this.currentVideoId ? this.timelineVideos.get(this.currentVideoId) : null;
    if (currentEntry && strictCandidates.some((entry) => entry.id === currentEntry.id)) {
      const winningEntry = sorted[0];
      if (winningEntry.id !== currentEntry.id) {
        const currentRect = currentEntry.element.getBoundingClientRect();
        const winningRect = winningEntry.element.getBoundingClientRect();
        const delta = this.lastScrollDirection === 'down'
          ? winningRect.top - currentRect.top
          : currentRect.bottom - winningRect.bottom;

        if (delta < this.centerLineHysteresisPx) {
          return currentEntry;
        }
      }
    }

    return sorted[0];
  }

  private syncActiveVideo(nextVideoId: string | null): void {
    this.pauseNonActiveVideos(nextVideoId);

    if (this.currentVideoId && this.currentVideoId !== nextVideoId) {
      const current = this.timelineVideos.get(this.currentVideoId);
      current?.element.pause();
      this.currentVideoId = null;
    }

    if (!nextVideoId) {
      return;
    }

    const nextEntry = this.timelineVideos.get(nextVideoId);
    if (!nextEntry) {
      return;
    }

    if (this.currentVideoId === nextVideoId) {
      nextEntry.element.muted = this.muted;
      return;
    }

    this.currentVideoId = nextVideoId;
    nextEntry.element.muted = this.muted;
    this.requestAutoplay(nextEntry);
  }

  private pauseCurrentVideo(): void {
    this.pauseNonActiveVideos(null);

    if (!this.currentVideoId) {
      return;
    }

    const current = this.timelineVideos.get(this.currentVideoId);
    current?.element.pause();
    this.currentVideoId = null;
  }

  private pauseNonActiveVideos(activeVideoId: string | null): void {
    this.timelineVideos.forEach((entry) => {
      const {element, id} = entry;
      if (id === activeVideoId) {
        return;
      }
      element.pause();
      this.cancelPendingAutoplay(entry);
    });
  }

  private releaseTimelineDecoder(entry: RegisteredTimelineVideo): void {
    this.cancelPendingAutoplay(entry);
    const hadDecoder = !!this.players[entry.id] || !!this.hlses[entry.id];
    // A registered viewport candidate can have a native source before its
    // asynchronous Plyr/HLS initialization completes. It still needs native
    // teardown when virtualisation removes the card.
    this.releaseVideoDecoder(entry.id, entry.element);
    if (hadDecoder) {
      entry.onDecoderReleased?.();
    }
  }

  /**
   * Calling play() before HLS is fully ready is deliberate: browsers queue the
   * request against the media element. Waiting only for canplay proved racy,
   * because a cached manifest can emit it before the listener is registered.
   */
  private requestAutoplay(entry: RegisteredTimelineVideo): void {
    if (!this.isActiveAutoplayEntry(entry) || entry.autoplayState === 'attempting' || entry.autoplayState === 'playing') {
      return;
    }

    this.clearAutoplayReadySignals(entry);
    const attempt = ++entry.autoplayAttempt;
    entry.autoplayState = 'attempting';
    this.autoplayAttempts += 1;
    entry.element.muted = this.muted;

    let result: Promise<void> | void;
    try {
      result = entry.element.play();
    } catch (error) {
      this.handleAutoplayFailure(entry, attempt, error);
      return;
    }

    if (!result || typeof (result as Promise<void>).then !== 'function') {
      this.finishAutoplaySuccess(entry, attempt);
      return;
    }

    void (result as Promise<void>).then(
      () => this.finishAutoplaySuccess(entry, attempt),
      (error) => this.handleAutoplayFailure(entry, attempt, error),
    );
  }

  private finishAutoplaySuccess(entry: RegisteredTimelineVideo, attempt: number): void {
    if (!this.isActiveAutoplayEntry(entry) || entry.autoplayAttempt !== attempt) {
      return;
    }
    entry.autoplayState = 'playing';
    this.autoplayStarts += 1;
    this.clearAutoplayReadySignals(entry);
  }

  private handleAutoplayFailure(
    entry: RegisteredTimelineVideo,
    attempt: number,
    error: unknown,
  ): void {
    if (!this.isActiveAutoplayEntry(entry) || entry.autoplayAttempt !== attempt) {
      return;
    }
    this.autoplayRejections += 1;
    this.autoplayFailureReasons[this.resolveAutoplayFailureReason(error)] += 1;

    // Source attachment and the first decoded HLS fragment can legitimately
    // race several times. Keep retrying only while this exact card remains
    // the active candidate; cancellation/deactivation clears the state.
    entry.autoplayState = 'retry-pending';
    this.autoplayRetries += 1;
    this.bindAutoplayReadySignals(entry);
  }

  private bindAutoplayReadySignals(entry: RegisteredTimelineVideo): void {
    this.clearAutoplayReadySignals(entry);
    const retry = () => {
      if (!this.isActiveAutoplayEntry(entry) || entry.autoplayState !== 'retry-pending') {
        return;
      }
      this.clearAutoplayReadySignals(entry);
      entry.autoplayState = 'idle';
      this.requestAutoplay(entry);
    };
    entry.element.addEventListener('loadedmetadata', retry, {once: true});
    entry.element.addEventListener('canplay', retry, {once: true});
    entry.autoplayReadyCleanup = () => {
      entry.element.removeEventListener('loadedmetadata', retry);
      entry.element.removeEventListener('canplay', retry);
    };
    // Cached native media may already be ready before either listener is
    // attached. The timer is scoped to this active card and rebinds only while
    // it is waiting for a real readiness event.
    entry.autoplayRetryTimer = setTimeout(() => {
      entry.autoplayRetryTimer = undefined;
      if (!this.isActiveAutoplayEntry(entry) || entry.autoplayState !== 'retry-pending') {
        return;
      }
      if (entry.element.readyState >= HTMLMediaElement.HAVE_METADATA) {
        retry();
      } else {
        this.bindAutoplayReadySignals(entry);
      }
    }, 250);
  }

  private notifyAutoplayReady(videoKey: string, owner: HTMLVideoElement): void {
    const entry = this.timelineVideos.get(videoKey);
    if (!entry || entry.element !== owner || entry.autoplayState !== 'retry-pending') {
      return;
    }
    this.clearAutoplayReadySignals(entry);
    entry.autoplayState = 'idle';
    this.requestAutoplay(entry);
  }

  /**
   * `canplay` can happen before the HLS source transition has settled. Buffered
   * fragments are the reliable readiness signal for an active HLS element.
   */
  private handleBufferedFragment(videoKey: string, owner: HTMLVideoElement, hls: Hls): void {
    if (this.hlses[videoKey] !== hls
      || this.resourceOwners.get(videoKey) !== owner) {
      return;
    }
    this.hlsBufferedFragments += 1;
    const mode = this.decoderModes.get(videoKey);
    if (mode === 'warm') {
      if (!this.warmBufferedVideoKeys.has(videoKey) && this.hasBufferedAhead(owner, this.warmBufferTargetSeconds)) {
        this.warmBufferedVideoKeys.add(videoKey);
        this.primeWarmDecoder(videoKey, owner);
      }
      return;
    }
    if (mode !== 'active' || this.bufferedAutoplayAttempts.has(videoKey)) {
      return;
    }
    // Gallery playback is deliberately owned by MediaView's readiness gate.
    // Calling native play here races a warm decoder being resumed and is the
    // source of the visible spinner after a fast slide change.
    if (this.resourceScopes.has(videoKey)) {
      this.resolveDecoderReadinessWaiters(videoKey, true);
      return;
    }
    this.bufferedAutoplayAttempts.add(videoKey);
    const entry = this.timelineVideos.get(videoKey);
    if (entry?.element === owner) {
      this.notifyAutoplayReady(videoKey, owner);
      if (this.currentVideoId === videoKey && entry.autoplayState !== 'playing' && entry.autoplayState !== 'attempting') {
        this.requestAutoplay(entry);
      }
      return;
    }
    owner.muted = this.muted;
    try {
      const playback = owner.play();
      if (playback && typeof (playback as Promise<void>).catch === 'function') {
        void (playback as Promise<void>).catch(() => {});
      }
    } catch {
      // The component-level active-mode retry remains available for a source
      // replacement racing with the first buffered fragment.
    }
  }

  private waitForDecoderReadiness(videoKey: string, owner: HTMLVideoElement): Promise<boolean> {
    if (!this.isCurrentActiveDecoder(videoKey, owner)) {
      return Promise.resolve(false);
    }
    if (this.isPlaybackReady(videoKey, owner)) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      const waiters = this.decoderReadinessWaiters.get(videoKey) || new Set<DecoderReadinessWaiter>();
      let waiter: DecoderReadinessWaiter;
      const check = () => {
        if (!this.isCurrentActiveDecoder(videoKey, owner)) {
          this.removeDecoderReadinessWaiter(videoKey, waiter, false);
          return;
        }
        if (this.isPlaybackReady(videoKey, owner)) {
          this.removeDecoderReadinessWaiter(videoKey, waiter, true);
        }
      };
      const cleanup = () => {
        owner.removeEventListener('canplay', check);
        owner.removeEventListener('loadeddata', check);
        owner.removeEventListener('progress', check);
      };
      waiter = {owner, resolve, cleanup};
      waiters.add(waiter);
      this.decoderReadinessWaiters.set(videoKey, waiters);
      owner.addEventListener('canplay', check);
      owner.addEventListener('loadeddata', check);
      owner.addEventListener('progress', check);
      check();
    });
  }

  private isCurrentActiveDecoder(videoKey: string, owner: HTMLVideoElement): boolean {
    return this.resourceOwners.get(videoKey) === owner && this.decoderModes.get(videoKey) === 'active';
  }

  private isPlaybackReady(videoKey: string, owner: HTMLVideoElement): boolean {
    return owner.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
      || this.hasBufferedAhead(owner, 0.1)
      || (!this.hlses[videoKey] && owner.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA);
  }

  private primeWarmDecoder(videoKey: string, owner: HTMLVideoElement): void {
    if (!this.resourceScopes.has(videoKey) || this.warmDecoderPrimeCleanup.has(videoKey)) {
      return;
    }
    let completed = false;
    const complete = () => {
      if (completed) {
        return;
      }
      completed = true;
      clearTimeout(timeout);
      owner.removeEventListener('playing', complete);
      this.warmDecoderPrimeCleanup.delete(videoKey);
      if (this.decoderModes.get(videoKey) !== 'warm' || this.resourceOwners.get(videoKey) !== owner) {
        return;
      }
      owner.pause();
      try {
        owner.currentTime = 0;
      } catch {
        // Metadata may still settle after HLS appends the initial fragment.
      }
    };
    const timeout = setTimeout(complete, 250);
    owner.addEventListener('playing', complete, {once: true});
    this.warmDecoderPrimeCleanup.set(videoKey, () => {
      clearTimeout(timeout);
      owner.removeEventListener('playing', complete);
    });
    owner.muted = true;
    owner.defaultMuted = true;
    try {
      const playback = owner.play();
      if (playback && typeof (playback as Promise<void>).catch === 'function') {
        void (playback as Promise<void>).catch(complete);
      }
    } catch {
      complete();
    }
  }

  private clearWarmDecoderPrime(videoKey: string): void {
    const cleanup = this.warmDecoderPrimeCleanup.get(videoKey);
    cleanup?.();
    this.warmDecoderPrimeCleanup.delete(videoKey);
  }

  private hasBufferedAhead(owner: HTMLVideoElement, minimumSeconds: number): boolean {
    const time = Number.isFinite(owner.currentTime) ? owner.currentTime : 0;
    for (let index = 0; index < owner.buffered.length; index += 1) {
      const start = owner.buffered.start(index);
      const end = owner.buffered.end(index);
      if (start <= time + 0.05 && end - time >= minimumSeconds) {
        return true;
      }
      if (Number.isFinite(owner.duration) && owner.duration > 0 && start <= time + 0.05 && end >= owner.duration - 0.05) {
        return true;
      }
    }
    return false;
  }

  private hasBufferedThrough(owner: HTMLVideoElement, targetEnd: number): boolean {
    for (let index = 0; index < owner.buffered.length; index += 1) {
      if (owner.buffered.start(index) <= 0.05 && owner.buffered.end(index) >= targetEnd - 0.05) {
        return true;
      }
    }
    return false;
  }

  private removeDecoderReadinessWaiter(videoKey: string, waiter: DecoderReadinessWaiter, ready: boolean): void {
    const waiters = this.decoderReadinessWaiters.get(videoKey);
    if (!waiters?.delete(waiter)) {
      return;
    }
    waiter.cleanup();
    waiter.resolve(ready);
    if (!waiters.size) {
      this.decoderReadinessWaiters.delete(videoKey);
    }
  }

  private resolveDecoderReadinessWaiters(videoKey: string, ready: boolean): void {
    const waiters = this.decoderReadinessWaiters.get(videoKey);
    if (!waiters) {
      return;
    }
    this.decoderReadinessWaiters.delete(videoKey);
    waiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.resolve(ready);
    });
  }

  private clearAutoplayReadySignals(entry: RegisteredTimelineVideo): void {
    entry.autoplayReadyCleanup?.();
    entry.autoplayReadyCleanup = undefined;
    if (entry.autoplayRetryTimer) {
      clearTimeout(entry.autoplayRetryTimer);
      entry.autoplayRetryTimer = undefined;
    }
  }

  private cancelPendingAutoplay(entry: RegisteredTimelineVideo): void {
    entry.autoplayAttempt += 1;
    entry.autoplayState = 'idle';
    this.clearAutoplayReadySignals(entry);
  }

  private isActiveAutoplayEntry(entry: RegisteredTimelineVideo): boolean {
    return this.currentVideoId === entry.id && this.timelineVideos.get(entry.id) === entry;
  }

  private resolveAutoplayFailureReason(error: unknown): 'abort' | 'notAllowed' | 'source' | 'other' {
    const name = typeof error === 'object' && error ? String((error as {name?: string}).name || '') : '';
    if (name === 'AbortError') {
      return 'abort';
    }
    if (name === 'NotAllowedError') {
      return 'notAllowed';
    }
    if (name === 'NotSupportedError' || name === 'NetworkError') {
      return 'source';
    }
    return 'other';
  }

  private resumeHlsDecoder(videoKey: string, videoElement: HTMLVideoElement, shouldLoad: boolean = true): void {
    const hls = this.hlses[videoKey];
    const source = this.hlsSources.get(videoKey);
    if (!hls || !source) {
      return;
    }
    try {
      const previousMode = this.decoderModes.get(videoKey);
      videoElement.muted = this.muted;
      videoElement.defaultMuted = true;
      videoElement.autoplay = shouldLoad;
      if (shouldLoad) {
        const alreadyPlayable = this.isPlaybackReady(videoKey, videoElement);
        if (previousMode !== 'active') {
          this.bufferedAutoplayAttempts.delete(videoKey);
        }
        this.decoderModes.set(videoKey, 'active');
        if (previousMode === 'warm') {
          this.clearWarmDecoderPrime(videoKey);
          // A neighbour with a decoded first frame and a contiguous warm
          // buffer can play directly from MSE. Calling startLoad() here adds
          // an avoidable scheduler turn even when every segment is cached.
          if (!alreadyPlayable) {
            this.warmBufferedVideoKeys.delete(videoKey);
            this.startHlsLoading(hls);
          } else {
            this.warmBufferedVideoKeys.add(videoKey);
          }
        }
      } else if (previousMode === 'active') {
        // Keep the attached source and bounded buffer for warm neighbours.
        this.decoderModes.set(videoKey, 'warm');
        if (!this.hasBufferedAhead(videoElement, this.warmBufferTargetSeconds)) {
          this.startHlsLoading(hls);
        } else {
          this.primeWarmDecoder(videoKey, videoElement);
        }
      } else {
        this.decoderModes.set(videoKey, 'warm');
      }
    } catch {
      // Leave playback paused if HLS cannot be resumed; the next lifecycle pass
      // will either retry or recreate the card normally.
    }
  }

  private getTrackedVideos(): RegisteredTimelineVideo[] {
    // The virtual list already bounds this set to mounted cards. Each keeps a
    // stable source until unmounted; autoplay only chooses which one plays.
    return [...this.timelineVideos.values()];
  }

  private isCenterLineInsideRect(rect: DOMRect, viewportMid: number, marginPx: number = 0): boolean {
    return rect.top <= viewportMid + marginPx && rect.bottom >= viewportMid - marginPx;
  }

  private getViewportMidpoint(): number {
    return this.getViewportHeight() / 2;
  }

  private getViewportHeight(): number {
    const visualViewport = window.visualViewport;
    return visualViewport?.height || window.innerHeight || document.documentElement?.clientHeight || 0;
  }

  private startNativePlayback(videoElement: HTMLVideoElement): void {
    try {
      const result = videoElement.play();
      if (result && typeof (result as Promise<void>).catch === 'function') {
        void (result as Promise<void>).catch(() => undefined);
      }
    } catch {
      // A later modal activation retries through Plyr if this browser rejects
      // the first muted play request.
    }
  }

  private startHlsLoading(hls: Hls, startPosition?: number): void {
    this.hlsStartLoadCalls += 1;
    hls.startLoad(startPosition);
  }

  private stopHlsLoading(hls: Hls | undefined): void {
    if (!hls) {
      return;
    }
    this.hlsStopLoadCalls += 1;
    hls.stopLoad();
  }

  private getScrollDirection(): 'up' | 'down' {
    const currentScroll = this.getScrollPosition();
    const lastScroll = this.lastScroll ?? currentScroll;
    this.lastScroll = currentScroll;
    return currentScroll > lastScroll ? 'down' : 'up';
  }

  private getScrollPosition(): number {
    const visualViewport = window.visualViewport as VisualViewport & { pageTop?: number } | undefined;
    return window.scrollY ?? visualViewport?.pageTop ?? visualViewport?.offsetTop ?? 0;
  }

  private ensureIntersectionObserver(): void {
    if (this.intersectionObserver || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target as HTMLVideoElement;
        const registered = this.timelineVideos.get(target.id);
        if (!registered) {
          return;
        }

      });

      this.scheduleViewportEvaluation();
    }, {
      root: null,
      rootMargin: this.autoplayRootMargin,
      threshold: 0,
    });
  }

  private destroyIntersectionObserver(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  private ensureViewportListeners(): void {
    if (!this.isBrowserEnvironment() || this.viewportListenersBound) {
      return;
    }

    window.addEventListener('resize', this.boundViewportChange, {passive: true});
    window.addEventListener('orientationchange', this.boundViewportChange, {passive: true});
    window.visualViewport?.addEventListener('resize', this.boundViewportChange, {passive: true});
    window.visualViewport?.addEventListener('scroll', this.boundViewportChange, {passive: true});
    this.viewportListenersBound = true;
  }

  private destroyViewportListeners(): void {
    if (!this.isBrowserEnvironment() || !this.viewportListenersBound) {
      return;
    }

    window.removeEventListener('resize', this.boundViewportChange);
    window.removeEventListener('orientationchange', this.boundViewportChange);
    window.visualViewport?.removeEventListener('resize', this.boundViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.boundViewportChange);
    this.viewportListenersBound = false;
  }

  private cancelViewportEvaluation(): void {
    if (this.animationFrameId === null || typeof cancelAnimationFrame !== 'function') {
      this.animationFrameId = null;
      return;
    }

    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
  }

  private isBrowserEnvironment(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  private syncMuteState(muted: boolean): void {
    Object.values(this.players).forEach((player) => {
      player.muted = muted;
    });

    this.timelineVideos.forEach(({element}) => {
      element.muted = muted;
    });

    void this.embeddedVideoPlayer.setGlobalMuted(muted);
  }

  private updateVolumeButtonsWatch(muted: boolean): void {
    if (muted) {
      void this.volumeButtons.startWatching();
      return;
    }

    void this.volumeButtons.stopWatching();
  }
}
